import { Sparkles, Palette } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import NextNimi from "@/components/home/NextNimi";
import ReferFriendCard from "@/components/home/ReferFriendCard";
import UnlimitedStatusCard from "@/components/profile/UnlimitedStatusCard";
import DynamicBanner from "@/components/home/DynamicBanner";
import { useHomepageBanners } from "@/hooks/useHomepageBanners";
import { useHostingNow } from "@/hooks/useHostingNow";
import HostingNowCard from "@/components/home/HostingNowCard";
import FeedbackReminderCard from "@/components/home/FeedbackReminderCard";

const Home = () => {
  const { hostingEvents, unmarkedByEvent } = useHostingNow();
  const { data: nextEvent } = useQuery({
    queryKey: ['next-event'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const now = new Date().toISOString();
      const { data: events } = await supabase
        .from('tickets')
        .select(`
          events (
            id,
            title,
            venue,
            start_time
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'confirmed') // only confirmed event
        .gte('events.start_time', now)
        .order('start_time', { referencedTable: 'events', ascending: true })
        .limit(1)
        .maybeSingle();

      if (!events) return null;
      return events.events;
    },
  });

  const { data: quote } = useQuery({
    queryKey: ['daily-quote'],
    queryFn: async () => {
      // In a real app, this would fetch from an API or database
      const quotes = [
        "Art enables us to find ourselves and lose ourselves at the same time. - Thomas Merton",
        "Creativity takes courage. - Henri Matisse",
        "Every artist was first an amateur. - Ralph Waldo Emerson",
        "Art is not what you see, but what you make others see. - Edgar Degas",
        "The purpose of art is washing the dust of daily life off our souls. - Pablo Picasso"
      ];
      return quotes[Math.floor(Math.random() * quotes.length)];
    },
  });

  const { data: artFact } = useQuery({
    queryKey: ['art-fact'],
    queryFn: async () => {
      // In a real app, this would fetch from an API or database
      const facts = [
        "Vincent van Gogh only sold one painting in his lifetime, 'The Red Vineyard,' despite creating over 2,100 artworks.",
        "Leonardo da Vinci wrote his notes backward, from right to left, in mirror writing. Some believe he did this to protect his ideas.",
        "The Mona Lisa has no eyebrows. It was the fashion in Renaissance Florence to shave them off.",
        "Pablo Picasso created over 147,000 artworks in his lifetime, including 13,500 paintings and 100,000 prints and engravings.",
        "The world's most expensive painting is Leonardo da Vinci's Salvator Mundi, sold for $450.3 million in 2017."
      ];
      return facts[Math.floor(Math.random() * facts.length)];
    },
  });

  const { data: banners } = useHomepageBanners();

  return (
    <div className="p-4 max-w-md mx-auto animate-fade-in">
      {hostingEvents.map((e) => (
        <div key={e.id} className="mb-6">
          <HostingNowCard event={e} unmarkedCount={unmarkedByEvent[e.id] ?? 0} />
        </div>
      ))}

      {/* Unlimited Status */}
      <UnlimitedStatusCard compact />

      <FeedbackReminderCard />
      
      {/* next event */}
      <div className="mt-6">
        <NextNimi event={nextEvent} />
      </div>
      
      {/* Dynamic Homepage Banners */}
      {banners?.map(banner => (
        <div key={banner.id} className="mt-6">
          <DynamicBanner banner={banner} />
        </div>
      ))}

      {/* Refer a Friend Card */}
      <div className="mt-6">
        <ReferFriendCard />
      </div>
      
      <div className="bg-card rounded-lg p-4 shadow-md mb-6 mt-6">
        <div className="flex items-center mb-2">
          <Sparkles className="text-secondary mr-2" />
          <h2 className="text-lg font-semibold text-card-foreground">Quick Muse</h2>
        </div>
        <p className="text-muted-foreground italic">{quote}</p>
      </div>
      
      <div className="bg-card rounded-lg p-4 shadow-md">
        <div className="flex items-center mb-2">
          <Palette className="text-secondary mr-2" />
          <h2 className="text-lg font-semibold text-card-foreground">A Little Art Scoop</h2>
        </div>
        <p className="text-muted-foreground">{artFact}</p>
      </div>
    </div>
  );
};

export default Home;
